/* eslint-disable max-lines -- file is large due to co-located internal components; refactoring is tracked */
/**
 * FilterPanel — orchestrates all filter pills, visibility state, and the "Dodaj filtr" dropdown.
 * See filter-pill-primitives.tsx for the full architecture overview and how to add new filter types.
 *
 * Internal components (not exported):
 *   FilterPill      — multi-select Command-list pill for SelectFilterField (tightly coupled to options[])
 *   AddFilterButton — the "+ Dodaj filtr" button listing hidden fields/items
 *
 * For custom filter UIs use CustomFilterPill from filter-pill-primitives (re-exported below).
 */

import * as React from 'react';
import { Check, ChevronDown, Plus, X } from 'lucide-react';
import { cn } from '../lib/utils.js';
import { Popover, PopoverContent, PopoverTrigger } from './popover.js';
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from './command.js';
import { DateRangePill } from './date-range-pill.js';
import { RangePill } from './range-pill.js';
import { Input } from './input.js';
import { Label } from './label.js';
import {
  PILL_BUTTON_BASE,
  PILL_BUTTON_IDLE,
  PILL_BUTTON_ACTIVE,
  PILL_VALUE_CLASS,
  PILL_VALUE_IDLE_CLASS,
  PillWrapper,
  PillTriggerButton,
} from './filter-pill-primitives.js';

// ─── Re-exports ───────────────────────────────────────────────
// All consumers should import types from here (or directly from filter-pill-primitives).

export type {
  ChipColor,
  SelectFilterField,
  TextFilterField,
  DateRangeFilterField,
  GroupedSelectOption,
  FilterOptionGroup,
  GroupedSelectFilterField,
  RangeFilterField,
  FilterField,
  FilterPanelLabels,
  FilterPanelProps,
  ExtraFilterItem,
  ExcludeToggleLabels,
  RangePillLabels,
} from './filter-pill-primitives.js';

export {
  PILL_BUTTON_BASE,
  PILL_BUTTON_IDLE,
  PILL_BUTTON_ACTIVE,
  PILL_VALUE_CLASS,
  PILL_VALUE_IDLE_CLASS,
  PillWrapper,
  CustomFilterPill,
} from './filter-pill-primitives.js';

export type { CustomFilterPillProps } from './filter-pill-primitives.js';

export { DateRangePill } from './date-range-pill.js';
export type { DateRangePillProps } from './date-range-pill.js';

export { RangePill, formatRangeValue } from './range-pill.js';
export type { RangePillProps } from './range-pill.js';

export { GroupedFilterPill } from './grouped-filter-pill.js';
export type { GroupedFilterPillProps } from './grouped-filter-pill.js';

import type {
  FilterField,
  SelectFilterField,
  TextFilterField,
  DateRangeFilterField,
  GroupedSelectFilterField,
  RangeFilterField,
  FilterPanelProps,
  ExcludeToggleLabels,
  RangePillLabels,
} from './filter-pill-primitives.js';
import { GroupedFilterPill } from './grouped-filter-pill.js';

// ─── FilterPill (INTERNAL — do not export) ────────────────────
//
// Renders a multi-select Command-list pill for a SelectFilterField.
// Tightly coupled to SelectFilterField.options — not suitable for
// custom filter UIs. For custom pills, use CustomFilterPill instead.

interface FilterPillProps {
  field: SelectFilterField;
  selected: string[];
  onChange: (id: string, values: string[]) => void;
  onClear: (id: string) => void;
  isLoading?: boolean;
  labels: FilterPanelProps['labels'];
  autoOpen?: boolean;
  onAutoOpened?: () => void;
  /** When true, the popover shows an include/exclude toggle for this field. */
  excludable?: boolean;
  /** Whether this field is currently in exclude (negation) mode. */
  excluded?: boolean;
  /** Called when the user toggles include/exclude mode. */
  onToggleExclude?: () => void;
  /** Translation-free labels for the exclude toggle. */
  excludeLabels?: ExcludeToggleLabels;
}

function FilterPill({
  field,
  selected,
  onChange,
  onClear,
  isLoading,
  labels,
  autoOpen,
  onAutoOpened,
  excludable,
  excluded,
  onToggleExclude,
  excludeLabels,
}: FilterPillProps) {
  const [open, setOpen] = React.useState(false);

  React.useEffect(() => {
    if (autoOpen && !open) {
      setOpen(true);
      onAutoOpened?.();
    }
  }, [autoOpen, open]);

  const inputRef = React.useRef<HTMLInputElement>(null);
  const isActive = selected.length > 0;

  React.useEffect(() => {
    if (!open) return;
    const timer = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => window.clearTimeout(timer);
  }, [open]);

  function toggleOption(optValue: string) {
    const next = selected.includes(optValue)
      ? selected.filter((v) => v !== optValue)
      : [...selected, optValue];
    onChange(field.id, next);
  }

  function handleClear(e: React.MouseEvent | React.KeyboardEvent) {
    e.stopPropagation();
    onClear(field.id);
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PillWrapper label={field.label} showLabel={isActive}>
        <PopoverTrigger asChild>
          <button
            type="button"
            role="combobox"
            aria-expanded={open}
            data-testid={`filter-trigger-${field.id}`}
            disabled={isLoading}
            className={cn(PILL_BUTTON_BASE, isActive ? PILL_BUTTON_ACTIVE : PILL_BUTTON_IDLE)}
          >
            {isActive ? (
              <>
                <span className="flex flex-wrap items-center gap-1 text-[11px] text-foreground">
                  {excluded ? (
                    <span aria-hidden className="font-semibold text-foreground/70">
                      ≠
                    </span>
                  ) : null}
                  {selected.map((v) => {
                    const optLabel = field.options.find((o) => o.value === v)?.label ?? v;
                    return (
                      <span key={v} className={PILL_VALUE_CLASS}>
                        {optLabel}
                      </span>
                    );
                  })}
                </span>
                <span
                  role="button"
                  tabIndex={0}
                  aria-label={labels.getClearAriaLabel(field.id)}
                  onClick={handleClear}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleClear(e);
                  }}
                  className="shrink-0 hover:opacity-70 rounded-full p-0.5"
                >
                  <X className="h-3 w-3 text-foreground/60" />
                </span>
              </>
            ) : (
              <>
                <span className={cn(PILL_VALUE_IDLE_CLASS, 'text-muted-foreground')}>
                  {field.label}
                </span>
                <ChevronDown className="h-3 w-3 text-muted-foreground shrink-0" />
              </>
            )}
          </button>
        </PopoverTrigger>
      </PillWrapper>
      <PopoverContent
        className="w-52 p-0"
        align="start"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        {excludable && excludeLabels ? (
          <div className="flex items-center gap-1 border-b border-border/60 p-1.5">
            <button
              type="button"
              data-testid={`filter-include-toggle-${field.id}`}
              aria-pressed={!excluded}
              onClick={() => {
                if (excluded) onToggleExclude?.();
              }}
              className={cn(
                'flex-1 rounded px-2 py-1 text-[11px] font-medium transition-colors',
                !excluded ? 'bg-muted text-foreground' : 'text-muted-foreground hover:bg-accent/50',
              )}
            >
              {excludeLabels.includeLabel}
            </button>
            <button
              type="button"
              data-testid={`filter-exclude-toggle-${field.id}`}
              aria-pressed={excluded}
              aria-label={excludeLabels.getToggleAriaLabel(field.id)}
              onClick={() => {
                if (!excluded) onToggleExclude?.();
              }}
              className={cn(
                'flex-1 rounded px-2 py-1 text-[11px] font-medium transition-colors',
                excluded ? 'bg-muted text-foreground' : 'text-muted-foreground hover:bg-accent/50',
              )}
            >
              {excludeLabels.excludeLabel}
            </button>
          </div>
        ) : null}
        <Command>
          <CommandInput ref={inputRef} placeholder={labels.searchPlaceholder} />
          <CommandList>
            <CommandEmpty>{/* No results */}</CommandEmpty>
            <CommandGroup>
              {field.options.map((opt) => {
                const checked = selected.includes(opt.value);
                return (
                  <CommandItem
                    key={opt.value}
                    value={opt.label}
                    onSelect={() => {
                      toggleOption(opt.value);
                      inputRef.current?.focus();
                    }}
                  >
                    <Check
                      className={cn('h-3.5 w-3.5 shrink-0', checked ? 'opacity-100' : 'opacity-0')}
                    />
                    {opt.label}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// ─── TextPill (INTERNAL — do not export) ───────────────────────
//
// Single-input text pill for TextFilterField. Keyboard-first: autofocus on
// open, Enter closes the popover (value already committed on every
// keystroke via onChange — same commit-on-Enter UX as RangePill), Escape's
// default close behavior leaves the already-committed value untouched.

interface TextPillProps {
  field: TextFilterField;
  /** [typedValue] or [] when cleared. */
  selected: string[];
  onChange: (id: string, values: string[]) => void;
  onClear: (id: string) => void;
  isLoading?: boolean;
  labels: FilterPanelProps['labels'];
  autoOpen?: boolean;
  onAutoOpened?: () => void;
}

function TextPill({
  field,
  selected,
  onChange,
  onClear,
  isLoading,
  labels,
  autoOpen,
  onAutoOpened,
}: TextPillProps) {
  const [open, setOpen] = React.useState(false);
  const value = selected[0] ?? '';
  const isActive = value.trim() !== '';
  const inputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (!autoOpen || open) return;
    const timer = window.setTimeout(() => {
      setOpen(true);
      onAutoOpened?.();
    }, 100);
    return () => window.clearTimeout(timer);
  }, [autoOpen, open]);

  function handleClear(e: React.MouseEvent | React.KeyboardEvent) {
    e.stopPropagation();
    onClear(field.id);
  }

  function handleCommitKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    setOpen(false);
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PillWrapper label={field.label} showLabel={isActive}>
        <PopoverTrigger asChild>
          <PillTriggerButton
            fieldId={field.id}
            isActive={isActive}
            isLoading={isLoading}
            open={open}
            idleLabel={field.label}
            activeContent={<span className={PILL_VALUE_CLASS}>{value}</span>}
            onClear={handleClear}
            clearAriaLabel={labels.getClearAriaLabel(field.id)}
          />
        </PopoverTrigger>
      </PillWrapper>
      <PopoverContent
        className="w-64 p-3"
        align="start"
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          inputRef.current?.focus();
        }}
      >
        <Label htmlFor={`${field.id}-input`}>{field.label}</Label>
        <Input
          ref={inputRef}
          id={`${field.id}-input`}
          type="text"
          value={value}
          placeholder={field.placeholder}
          onChange={(e) => onChange(field.id, e.target.value ? [e.target.value] : [])}
          onKeyDown={handleCommitKeyDown}
          className="mt-1"
          data-testid={`${field.id}-input`}
        />
      </PopoverContent>
    </Popover>
  );
}

// ─── Field types addable via "Dodaj filtr" (all types EXCEPT extra items) ──

type StandardFilterField =
  | SelectFilterField
  | DateRangeFilterField
  | GroupedSelectFilterField
  | TextFilterField
  | RangeFilterField;

const STANDARD_FIELD_TYPES = new Set<FilterField['type']>([
  'select',
  'dateRange',
  'groupedSelect',
  'text',
  'range',
]);

// ─── FilterFieldPill (INTERNAL — do not export) ────────────────
//
// Dispatches one visible FilterField to its pill component. Extracted from
// FilterPanel's render loop to keep FilterPanel itself within the file's
// max-lines-per-function budget.

interface FilterFieldPillProps {
  field: FilterField;
  value: Record<string, string[]>;
  onFieldChange: (id: string, values: string[]) => void;
  onClear: (id: string) => void;
  isLoading?: boolean;
  labels: FilterPanelProps['labels'];
  rangeLabels?: RangePillLabels;
  autoOpenId: string | null;
  onAutoOpened: () => void;
  excludableSet: Set<string>;
  excludedSet: Set<string>;
  onToggleExclude?: (fieldId: string) => void;
  excludeLabels?: ExcludeToggleLabels;
}

function FilterFieldPill({
  field,
  value,
  onFieldChange,
  onClear,
  isLoading,
  labels,
  rangeLabels,
  autoOpenId,
  onAutoOpened,
  excludableSet,
  excludedSet,
  onToggleExclude,
  excludeLabels,
}: FilterFieldPillProps) {
  const selected = value[field.id] ?? [];
  const autoOpen = autoOpenId === field.id;
  switch (field.type) {
    case 'dateRange':
      return (
        <DateRangePill
          field={field}
          selected={selected}
          onChange={onFieldChange}
          onClear={onClear}
          isLoading={isLoading}
          labels={labels}
          autoOpen={autoOpen}
          onAutoOpened={onAutoOpened}
        />
      );
    case 'select':
      return (
        <FilterPill
          field={field}
          selected={selected}
          onChange={onFieldChange}
          onClear={onClear}
          isLoading={isLoading}
          labels={labels}
          autoOpen={autoOpen}
          onAutoOpened={onAutoOpened}
          excludable={excludableSet.has(field.id)}
          excluded={excludedSet.has(field.id)}
          onToggleExclude={() => onToggleExclude?.(field.id)}
          excludeLabels={excludeLabels}
        />
      );
    case 'groupedSelect':
      return (
        <GroupedFilterPill
          field={field}
          selected={selected}
          onChange={onFieldChange}
          onClear={onClear}
          isLoading={isLoading}
          labels={labels}
          autoOpen={autoOpen}
          onAutoOpened={onAutoOpened}
        />
      );
    case 'text':
      return (
        <TextPill
          field={field}
          selected={selected}
          onChange={onFieldChange}
          onClear={onClear}
          isLoading={isLoading}
          labels={labels}
          autoOpen={autoOpen}
          onAutoOpened={onAutoOpened}
        />
      );
    case 'range':
      return (
        <RangePill
          field={field}
          selected={selected}
          onChange={onFieldChange}
          onClear={onClear}
          isLoading={isLoading}
          labels={labels}
          rangeLabels={rangeLabels as RangePillLabels}
          autoOpen={autoOpen}
          onAutoOpened={onAutoOpened}
        />
      );
    default:
      return null;
  }
}

// ─── AddFilterButton (INTERNAL — do not export) ───────────────
//
// The "+ Dodaj filtr" button. Lists both hidden standard fields AND
// hidden extraHiddenItems in a single Command list.

interface AddFilterButtonProps {
  hiddenFields: StandardFilterField[];
  onAdd: (id: string) => void;
  labels: FilterPanelProps['labels'];
}

function AddFilterButton({ hiddenFields, onAdd, labels }: AddFilterButtonProps) {
  const [open, setOpen] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (!open) return;
    const timer = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => window.clearTimeout(timer);
  }, [open]);

  if (hiddenFields.length === 0) return null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="add-filter-button"
          className={cn(PILL_BUTTON_BASE, PILL_BUTTON_IDLE, 'text-muted-foreground')}
        >
          <Plus className="h-3 w-3" />
          <span className={PILL_VALUE_IDLE_CLASS}>{labels.addFilterLabel ?? '+'}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-48 p-0" align="start">
        <Command>
          <CommandInput ref={inputRef} placeholder={labels.searchPlaceholder} />
          <CommandList>
            <CommandEmpty>{/* No results */}</CommandEmpty>
            <CommandGroup>
              {hiddenFields.map((f) => (
                <CommandItem
                  key={f.id}
                  value={f.label}
                  onSelect={() => {
                    onAdd(f.id);
                    setOpen(false);
                  }}
                >
                  {f.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// ─── Helpers ──────────────────────────────────────────────────

/** Field types whose `value` entry is a fixed-length tuple (e.g. [min, max]) where a
 * side can be blank — "has a value" means at least one element is non-empty, not
 * merely "array is non-empty" (a `['', '']` array is still idle). */
const TUPLE_VALUE_FIELD_TYPES = new Set(['dateRange', 'range']);

function fieldHasValue(fieldType: FilterField['type'], vals: string[]): boolean {
  return TUPLE_VALUE_FIELD_TYPES.has(fieldType) ? vals.some((v) => v !== '') : vals.length > 0;
}

function buildInitialVisibleIds(
  fields: FilterField[],
  value: Record<string, string[]>,
): Set<string> {
  const ids = new Set<string>();
  fields.forEach((f) => {
    const vals = value[f.id] ?? [];
    if (f.pinned || fieldHasValue(f.type, vals)) ids.add(f.id);
  });
  return ids;
}

// ─── FilterPanel (EXPORTED) ───────────────────────────────────

export function FilterPanel({
  fields,
  value,
  onChange,
  onClearAll,
  isLoading,
  labels,
  extraPills,
  extraHiddenItems = [],
  renderExtraPill,
  defaultVisibleExtraIds,
  onShowExtra,
  excludableFieldIds,
  excludedFieldIds,
  onToggleExclude,
  excludeLabels,
  rangeLabels,
  leadingContent,
}: FilterPanelProps & {
  /**
   * Rendered as the FIRST child inside FilterPanel's own `flex flex-wrap` row
   * (mockup-only experiment — see apps/mockups/src/screens/media-inventory.mockup.tsx).
   * Lets a caller-owned control (e.g. a search box) share the exact same
   * flex-wrap container as the filter pills, so it wraps as one group with
   * them instead of living in a separate sibling row that wraps independently.
   */
  leadingContent?: React.ReactNode;
}) {
  const excludableSet = React.useMemo(
    () => new Set(excludableFieldIds ?? []),
    [excludableFieldIds],
  );
  const excludedSet = React.useMemo(() => new Set(excludedFieldIds ?? []), [excludedFieldIds]);
  const [visibleFilterIds, setVisibleFilterIds] = React.useState<Set<string>>(() =>
    buildInitialVisibleIds(fields, value),
  );

  // Tracks which extraHiddenItems are visible; seeded from defaultVisibleExtraIds for URL restore.
  const [visibleExtraIds, setVisibleExtraIds] = React.useState<Set<string>>(
    () => new Set(defaultVisibleExtraIds ?? []),
  );

  // Ref so the useEffect below can read latest value without stale closure
  const valueRef = React.useRef(value);
  valueRef.current = value;

  // Seed pinned fields and any with existing values when fields arrive asynchronously.
  // Only runs when visibleFilterIds is still empty to avoid overwriting user actions.
  React.useEffect(() => {
    if (fields.length === 0) return;
    setVisibleFilterIds((prev) => {
      if (prev.size > 0) return prev;
      return buildInitialVisibleIds(fields, valueRef.current);
    });
  }, [fields]);

  function handleFieldChange(id: string, values: string[]) {
    onChange({ ...value, [id]: values });
  }

  function handleClear(id: string) {
    const field = fields.find((f) => f.id === id);
    const isPinned = field?.pinned ?? false;
    onChange({ ...value, [id]: [] });
    if (!isPinned) {
      setVisibleFilterIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  }

  const autoOpenIdRef = React.useRef<string | null>(null);
  const [, forceRender] = React.useState(0);
  // Holds the pending auto-open nudge timer so it can be cleared on unmount
  // (or before re-scheduling), preventing a setState on an unmounted component.
  const autoOpenTimerRef = React.useRef<number | null>(null);

  React.useEffect(() => {
    return () => {
      if (autoOpenTimerRef.current !== null) {
        window.clearTimeout(autoOpenTimerRef.current);
        autoOpenTimerRef.current = null;
      }
    };
  }, []);

  function handleAddFilter(id: string) {
    // Route to extra items first (they don't live in `value`)
    if (extraHiddenItems.some((item) => item.id === id)) {
      onShowExtra?.(id);
      setVisibleExtraIds((prev) => new Set([...prev, id]));
      return;
    }
    // Standard field: mark visible and auto-open its popover
    autoOpenIdRef.current = id;
    setVisibleFilterIds((prev) => new Set([...prev, id]));
    // Clear any prior pending nudge before scheduling a new one.
    if (autoOpenTimerRef.current !== null) {
      window.clearTimeout(autoOpenTimerRef.current);
    }
    autoOpenTimerRef.current = window.setTimeout(() => {
      autoOpenTimerRef.current = null;
      forceRender((n) => n + 1);
    }, 50);
  }

  function handleHideExtra(id: string) {
    setVisibleExtraIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }

  function handleClearAll() {
    const cleared: Record<string, string[]> = { ...value };
    for (const f of fields) {
      cleared[f.id] = [];
    }
    onChange(cleared);
    onClearAll?.();
    setVisibleFilterIds(new Set(fields.filter((f) => f.pinned).map((f) => f.id)));
    setVisibleExtraIds(new Set());
  }

  const hasAnyActive =
    fields.some((f) => fieldHasValue(f.type, value[f.id] ?? [])) || visibleExtraIds.size > 0;

  const visibleFields = fields.filter((f) => visibleFilterIds.has(f.id));

  const hiddenStandardFields = fields.filter(
    (f): f is StandardFilterField =>
      !visibleFilterIds.has(f.id) && STANDARD_FIELD_TYPES.has(f.type),
  );

  // Merge hidden standard fields + hidden extra items into one list for "Dodaj filtr"
  const hiddenExtraItems = extraHiddenItems.filter((item) => !visibleExtraIds.has(item.id));
  const hiddenAddableFields: StandardFilterField[] = [
    ...hiddenStandardFields,
    // Cast extra items as minimal SelectFilterField so AddFilterButton can render them.
    // options:[] is intentional — AddFilterButton only needs id+label for the menu entry.
    ...hiddenExtraItems.map((item) => ({
      type: 'select' as const,
      id: item.id,
      label: item.label,
      options: [],
    })),
  ];

  return (
    <div className="flex items-end gap-2 flex-wrap" data-testid="filter-panel">
      {leadingContent}
      {visibleFields.map((field) => (
        <FilterFieldPill
          key={field.id}
          field={field}
          value={value}
          onFieldChange={handleFieldChange}
          onClear={handleClear}
          isLoading={isLoading}
          labels={labels}
          rangeLabels={rangeLabels}
          autoOpenId={autoOpenIdRef.current}
          onAutoOpened={() => {
            autoOpenIdRef.current = null;
          }}
          excludableSet={excludableSet}
          excludedSet={excludedSet}
          onToggleExclude={onToggleExclude}
          excludeLabels={excludeLabels}
        />
      ))}

      {/* Legacy extraPills slot — prefer extraHiddenItems+renderExtraPill for new pills */}
      {extraPills}

      {/* Visible extra pills (shown after user selects them from "Dodaj filtr") */}
      {renderExtraPill != null &&
        [...visibleExtraIds].map((id) => (
          <React.Fragment key={id}>{renderExtraPill(id, () => handleHideExtra(id))}</React.Fragment>
        ))}

      <PillWrapper label="&nbsp;" showLabel={false}>
        <AddFilterButton
          hiddenFields={hiddenAddableFields}
          onAdd={handleAddFilter}
          labels={labels}
        />
      </PillWrapper>

      {hasAnyActive ? (
        <PillWrapper label="&nbsp;" showLabel={false}>
          <button
            type="button"
            onClick={handleClearAll}
            data-testid="filter-panel-clear-filters"
            className={cn(
              PILL_BUTTON_BASE,
              'border-transparent text-muted-foreground hover:text-foreground',
            )}
          >
            <span className={PILL_VALUE_IDLE_CLASS}>{labels.clearAllLabel}</span>
          </button>
        </PillWrapper>
      ) : null}
    </div>
  );
}
